import React from 'react';
import {
  Link as LinkIcon,
  Globe,
  MessageCircle,
  Phone,
  Mail,
  FileText,
} from 'lucide-react';
import { useSite } from '../../../../context/SiteContext';
import { ButtonLinkType } from '../../../../types';

interface ActionLinkInputProps {
  label?: string;
  buttonText?: string;
  onButtonTextChange?: (text: string) => void;
  linkType?: ButtonLinkType;
  onLinkTypeChange: (type: ButtonLinkType) => void;
  linkValue?: string;
  onLinkValueChange: (val: string) => void;
  whatsappPhone?: string;
  onWhatsappPhoneChange?: (phone: string) => void;
  whatsappMessage?: string;
  onWhatsappMessageChange?: (msg: string) => void;
  phoneNumber?: string;
  onPhoneNumberChange?: (phone: string) => void;
  emailAddress?: string;
  onEmailAddressChange?: (email: string) => void;
  hideButtonText?: boolean;
}

export const ActionLinkInput: React.FC<ActionLinkInputProps> = ({
  label = 'Buton & Eylem Bağlantısı',
  buttonText = 'Teklif Alın',
  onButtonTextChange,
  linkType = 'page',
  onLinkTypeChange,
  linkValue = '/iletisim',
  onLinkValueChange,
  whatsappPhone,
  onWhatsappPhoneChange,
  whatsappMessage,
  onWhatsappMessageChange,
  phoneNumber,
  onPhoneNumberChange,
  emailAddress,
  onEmailAddressChange,
  hideButtonText = false,
}) => {
  const { activeSite } = useSite();
  const pages = activeSite?.pages || [];

  const linkTypeOptions: Array<{ id: ButtonLinkType; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: 'page', label: 'Sayfa', icon: FileText },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { id: 'phone', label: 'Telefon', icon: Phone },
    { id: 'email', label: 'E-posta', icon: Mail },
    { id: 'url', label: 'Harici URL', icon: Globe },
  ];

  return (
    <div className="p-3.5 rounded-2xl bg-slate-800/60 border border-slate-700/80 space-y-3">
      <div className="flex items-center gap-2">
        <LinkIcon className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-xs font-bold text-slate-200">{label}</span>
      </div>

      {/* Button Label Input */}
      {!hideButtonText && onButtonTextChange && (
        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Buton Üzerindeki Yazı
          </label>
          <input
            type="text"
            value={buttonText || ''}
            onChange={(e) => onButtonTextChange(e.target.value)}
            placeholder="Örn: Hemen Başla / Teklif Al"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>
      )}

      {/* Action Type Selector */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Tıklanınca Ne Yapılsın?
        </label>
        <div className="grid grid-cols-3 gap-1.5">
          {linkTypeOptions.map((opt) => {
            const Icon = opt.icon;
            const isSelected = linkType === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onLinkTypeChange(opt.id)}
                className={`py-1.5 px-2 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition ${
                  isSelected
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-slate-900/80 text-slate-300 hover:bg-slate-700 border border-slate-700/60'
                }`}
              >
                <Icon className="w-3 h-3" />
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Dynamic Target Inputs based on linkType */}
      {linkType === 'page' && (
        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Gidilecek Sayfa
          </label>
          <select
            value={linkValue || (pages[0]?.slug ?? '/')}
            onChange={(e) => onLinkValueChange(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500 cursor-pointer"
          >
            {pages.map((p) => (
              <option key={p.id} value={p.slug}>
                {p.title} ({p.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {linkType === 'whatsapp' && (
        <div className="space-y-2">
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1">
              <MessageCircle className="w-3 h-3" />
              WhatsApp Numarası
            </label>
            <input
              type="text"
              value={whatsappPhone ?? activeSite?.settings?.socialLinks?.whatsapp ?? activeSite?.settings?.phone ?? ''}
              onChange={(e) => onWhatsappPhoneChange && onWhatsappPhoneChange(e.target.value)}
              placeholder="+90 532 000 00 00"
              className="w-full bg-slate-900 border border-emerald-500/40 text-xs font-semibold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-emerald-400"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Otomatik Mesaj Taslağı
            </label>
            <input
              type="text"
              value={whatsappMessage ?? ''}
              onChange={(e) => onWhatsappMessageChange && onWhatsappMessageChange(e.target.value)}
              placeholder="Merhaba, web sitenizden ulaşıyorum..."
              className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-emerald-400"
            />
          </div>
        </div>
      )}

      {linkType === 'phone' && (
        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-cyan-400 flex items-center gap-1">
            <Phone className="w-3 h-3" />
            Doğrudan Aranacak Numara
          </label>
          <input
            type="tel"
            value={phoneNumber ?? activeSite?.settings?.phone ?? ''}
            onChange={(e) => onPhoneNumberChange ? onPhoneNumberChange(e.target.value) : onLinkValueChange(e.target.value)}
            placeholder="+90 212 800 50 00"
            className="w-full bg-slate-900 border border-cyan-500/40 text-xs font-semibold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-cyan-400"
          />
        </div>
      )}

      {linkType === 'email' && (
        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            E-posta Adresi
          </label>
          <input
            type="email"
            value={emailAddress ?? activeSite?.settings?.email ?? ''}
            onChange={(e) => onEmailAddressChange ? onEmailAddressChange(e.target.value) : onLinkValueChange(e.target.value)}
            placeholder="info@sirketiniz.com"
            className="w-full bg-slate-900 border border-amber-500/40 text-xs font-semibold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-amber-400"
          />
        </div>
      )}

      {linkType === 'url' && (
        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Dış Web Bağlantısı
          </label>
          <input
            type="url"
            value={linkValue || ''}
            onChange={(e) => onLinkValueChange(e.target.value)}
            placeholder="https://example.com"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>
      )}
    </div>
  );
};
